import * as React from 'react'
import { cn } from '@repo/ui/utils'
import { Typography } from '@repo/ui/typography'
import { Globe, Mail, Map } from 'lucide-react'
import { Organization } from './OrganizationPage'

interface OrganizationProfileProps extends Organization {
  isSpecimenCenter?: boolean
}

const CARD_CLASSES = 'p-4 mb-6 bg-white rounded-2xl shadow'
const CONTAINER_CLASSES = 'flex flex-col grow w-full sm:max-w-[500px] lg:max-w-[1200px] self-center'

const ContactInfo: React.FC<Omit<OrganizationProfileProps, 'id'>> = ({ websiteUrl, contactEmail, address }) => (
  <div className={`${CARD_CLASSES} hidden lg:block`}>
    <Typography font="fragment" className="text-slate-900 mb-2">
      Contact Information
    </Typography>

    <div className="grid grid-cols-2 gap-6">
      <div>
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <Globe className="w-4 h-4 text-gray-400" />
          Website
        </div>
        {websiteUrl ? (
          <a
            href={websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-teal-700 hover:underline break-all text-xs"
          >
            {websiteUrl}
          </a>
        ) : (
          <span className="text-gray-400 text-sm">Not provided</span>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <Mail className="w-4 h-4 text-gray-400" />
          Email
        </div>
        {contactEmail ? (
          <a href={`mailto:${contactEmail}`} className="text-teal-700 hover:underline break-all text-xs">
            {contactEmail}
          </a>
        ) : (
          <span className="text-gray-400 text-xs">Not provided</span>
        )}
      </div>

      {address && (
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            <Map className="w-4 h-4 text-gray-400" />
            Address
          </div>

          <Typography size="sm" className="text-teal-700 text-xs">
            {address}
          </Typography>
        </div>
      )}
    </div>
  </div>
)

const OrganizationProfile: React.FC<OrganizationProfileProps> = ({ isSpecimenCenter = false, ...org }) => (
  <main className={cn(CONTAINER_CLASSES, { 'p-4': isSpecimenCenter })}>
    {/* Organization Summary Section */}
    <div className={`${CARD_CLASSES} mb-6`}>
      <Typography className="text-slate-900 mb-2">Summary</Typography>

      <Typography size="sm" className="text-gray-600">
        {org.description || 'No description available for this organization.'}
      </Typography>
    </div>

    <ContactInfo {...org} />
  </main>
)

export default OrganizationProfile
